// CheckoutForm.js — Stripe-hosted Checkout (Billing quickstart)
import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch } from 'react-redux'
import { makePayment } from 'src/features/SubscriptionSlice'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'

const CheckoutForm = ({ venueId, priceId, discountId, price }) => {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const dispatch = useDispatch()

  const handleSubmit = async e => {
    e.preventDefault()
    setMessage('')
    setLoading(true)

    try {
      const response = await dispatch(
        makePayment({
          venueId,
          priceId,
          discountId,
        }),
      )

      if (response?.error && !response?.data?.url) {
        setMessage(response.error)
        setLoading(false)
        return
      }

      const checkout = response?.data
      if (checkout?.checkoutMode === 'hosted' && checkout?.url) {
        setMessage('Redirecting to secure Stripe Checkout…')
        window.location.assign(checkout.url)
        return
      }

      setMessage(
        response?.message ||
          'Could not start Stripe Checkout. Please try again.',
      )
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Error starting checkout.')
    }

    setLoading(false)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormTitle>Payment Information</FormTitle>
      <FormPrice>
        {price ? `Total: £${(price / 100).toFixed(2)}` : ''}
      </FormPrice>

      <HelpText>
        You will complete payment on Stripe&apos;s secure checkout page. Your
        bank may ask for 3D Secure approval there.
      </HelpText>

      <SubmitButton type='submit' disabled={loading}>
        {loading ? (
          <>
            <Spinner />
            Starting Checkout…
          </>
        ) : (
          'Continue to Stripe Checkout'
        )}
      </SubmitButton>

      {message && (
        <Message $success={message.includes('Redirecting')}>{message}</Message>
      )}
      <GrowthEngineHumanDemoRow compact />
    </Form>
  )
}

const Form = styled.form`
  width: 100%;
  max-width: 600px;
  min-height: 320px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`

const FormTitle = styled.h2`
  color: #2d3748;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 600;
`

const FormPrice = styled.h3`
  color: ${({ theme }) => theme.colours.lettuceGreen};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 600;
`

const HelpText = styled.p`
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 2rem;
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Spinner = styled.span`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: ${spin} 1s ease-in-out infinite;
`

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
  }
`

export const Message = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
  background: ${props => (props.$success ? '#f0fff4' : '#fff5f5')};
  color: ${props => (props.$success ? '#2f855a' : '#c53030')};
  border: 1px solid ${props => (props.$success ? '#c6f6d5' : '#fed7d7')};
`

export default CheckoutForm
